import { Component, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { DialogService } from '@shared/services/dialog';
import { FormUtil, NomidioFormsService } from '@shared/forms';
import { Timeout } from '@nomidio/common';
import { DialogConfig } from '../dialog/dialog-config';
import { DialogIcon } from '../dialog/dialog-icon.enum';
import { DialogComponent } from '../dialog/dialog.component';
import { NonNullableFormBuilder } from '@angular/forms';
import { NomidioErrorsService } from '@shared/services/nomidio-component-errors/nomidio-errors.service';

@Component({
  selector: 'app-email-pin-page',
  templateUrl: './email-pin-page.component.html',
  styleUrls: ['./email-pin-page.component.scss'],
  standalone: false,
})
export class EmailPinPageComponent implements OnDestroy {
  private readonly dialog = inject(DialogService);
  private readonly nomForms = inject(NomidioFormsService);
  private readonly fb = inject(NonNullableFormBuilder);
  protected readonly errors = inject(NomidioErrorsService);

  private readonly SENDING_EMAIL_MSG_DELAY_MS: number = 10 * 1000; // 10 seconds
  private readonly RESEND_LIMIT_MSG_DELAY_MS: number = 10 * 1000; // 10 seconds

  public readonly form = this.fb.group({
    pin: this.nomForms.getOtpControl(),
  });
  public get pinInput() {
    return this.form.controls.pin;
  }

  public canResendEmail = true;
  public isSendingEmail = false;
  public sendEmailError = false;
  public sendEmailTimeout: Timeout;
  public emailResendLimitError = false;
  public emailResendLimitTimeout: Timeout;

  public couldNotVerifyError = false;
  public timeoutError = false;
  public showResentMessage = false;
  public busy = false;

  /**
   * The user's email, strictly for the purposes of displaying it to them.
   * No server interactions are performed with this email.
   * It is acceptable to censor this email.
   */
  @Input() email: string | undefined;

  /**
   * Emits a signal to resend the MFA email.
   * If the email request fails, {@link #requestEmailFailed()} must be called.
   */
  @Output() resendEmail = new EventEmitter<void>();

  /**
   * Emits a pin code the user is submitting. This should be verified by the server.
   * If the pin code is invalid, the handler must call {@link #tryAgainWithNewPin()} or {@link #couldNotVerifyFailure()}.
   */
  @Output() verifyPin = new EventEmitter<string>();

  /**
   * Trigger that the containing component should re-send an MFA code email to the user.
   */
  sendEmail(): void {
    this.isSendingEmail = true;
    this.sendEmailError = false;

    this.resendEmail.emit();

    this.sendEmailTimeout = setTimeout(() => {
      this.isSendingEmail = false;
    }, this.SENDING_EMAIL_MSG_DELAY_MS);
  }

  verify(): void {
    this.busy = true;
    this.timeoutError = false;
    this.couldNotVerifyError = false;
    this.showResentMessage = false;
    this.verifyPin.emit(this.pinInput.value);
  }

  public requestEmailFailed() {
    this.sendEmailError = true;
    this.isSendingEmail = false;
    clearTimeout(this.sendEmailTimeout);
  }

  public emailResendLimit(): void {
    this.canResendEmail = false;
    this.isSendingEmail = false;
    this.emailResendLimitError = true;
    clearTimeout(this.sendEmailTimeout);

    this.emailResendLimitTimeout = setTimeout(() => {
      this.emailResendLimitError = false;
    }, this.RESEND_LIMIT_MSG_DELAY_MS);
  }

  public incorrectPin() {
    this.pinInput.setValue('');
    FormUtil.forceError(this.pinInput, { retry: true });
    this.busy = false;
  }

  /**
   * Display to the user that a new email has been sent to the user.
   */
  public onVerificationResent() {
    this.showResentMessage = true;
    this.busy = false;
  }

  public tryAgainWithNewPin() {
    this.busy = false;
    this.pinInput.setValue('');
    this.sendEmail();

    const dialogData: DialogConfig = {
      icon: DialogIcon.AlertRed,
      content: 'You will receive an e-mail with a new 6 digit code',
      title: 'Invalid code.',
      primary: 'Ok',
    };

    this.dialog.open(DialogComponent, {
      autoFocus: true,
      data: dialogData,
      disableClose: false,
    });
  }

  public couldNotVerifyFailure() {
    this.busy = false;
    this.couldNotVerifyError = true;
  }

  public timeoutFailure(): void {
    this.busy = false;
    this.timeoutError = true;
    this.tryAgainWithNewPin();
  }

  ngOnDestroy() {
    clearTimeout(this.sendEmailTimeout);
  }
}
